import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import {
  Tag, Globe, RefreshCw, FileText, Download, ArrowRight,
  ArrowLeft, Check, Settings2, Sparkles, Crown, Zap,
  Link2, BarChart3, Shield, Moon
} from "lucide-react";

const DOMAINS = [
  { id: "amazon.com", label: "amazon.com", flag: "US", currency: "USD ($)" },
  { id: "amazon.in", label: "amazon.in", flag: "IN", currency: "INR (\u20B9)" },
  { id: "amazon.co.uk", label: "amazon.co.uk", flag: "UK", currency: "GBP (\u00A3)" },
];

const SYNC_OPTIONS = [
  { value: "4", label: "Every 4 hours" },
  { value: "6", label: "Every 6 hours" },
  { value: "8", label: "Every 8 hours (recommended)" },
  { value: "12", label: "Every 12 hours" },
  { value: "24", label: "Every 24 hours" },
];

const DEFAULT_CONFIG = {
  affiliate_tag: "",
  enabled_domains: ["amazon.com", "amazon.in", "amazon.co.uk"],
  sync_interval_hours: 8,
  sync_batch_size: 10,
  request_timeout: 15,
  price_history: true,
  disclaimer_text: "As an Amazon Associate, we earn from qualifying purchases.",
  version: "free",
  smart_sync: true,
  link_cloaking: true,
  link_prefix: "go",
  sticky_cta: true,
  exit_intent: false,
  countdown_enabled: false,
  dark_mode: false,
  schema_enabled: true,
};

export default function ConfigWizard({ open, onOpenChange, apiUrl, initialVersion = "free" }) {
  const [step, setStep] = useState(0);
  const [config, setConfig] = useState({ ...DEFAULT_CONFIG, version: initialVersion });
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (open) {
      setConfig((prev) => ({
        ...prev,
        version: initialVersion,
        sync_batch_size: initialVersion === "pro" ? 15 : 10,
      }));
      setStep(0);
    }
  }, [open, initialVersion]);

  const isPro = config.version === "pro";
  const STEPS = isPro
    ? [
        { icon: Tag, label: "Basic Setup" },
        { icon: Settings2, label: "Sync & Features" },
        { icon: Crown, label: "PRO Settings" },
        { icon: Download, label: "Download" },
      ]
    : [
        { icon: Tag, label: "Basic Setup" },
        { icon: Settings2, label: "Advanced" },
        { icon: Download, label: "Download" },
      ];
  const lastStep = STEPS.length - 1;

  const updateConfig = (key, value) => setConfig((prev) => ({ ...prev, [key]: value }));

  const toggleDomain = (domain) => {
    setConfig((prev) => {
      const domains = prev.enabled_domains.includes(domain)
        ? prev.enabled_domains.filter((d) => d !== domain)
        : [...prev.enabled_domains, domain];
      return { ...prev, enabled_domains: domains.length > 0 ? domains : [domain] };
    });
  };

  const [downloadError, setDownloadError] = useState("");

  const handleDownload = async () => {
    setDownloading(true);
    setDownloadError("");
    try {
      const res = await fetch(`${apiUrl}/plugin/download-custom`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(config),
      });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const blob = await res.blob();
      if (blob.size < 100) throw new Error("Invalid response from server");
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = isPro ? "amazon-affiliate-pro.zip" : "amazon-affiliate-plugin.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed:", err);
      setDownloadError("Download failed. Please try again or contact support.");
    }
    setDownloading(false);
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => {
      setStep(0);
      setConfig({ ...DEFAULT_CONFIG });
    }, 300);
  };

  const renderStepContent = () => {
    // Step 0: Basic Setup (both versions)
    if (step === 0) return (
      <div className="space-y-6 animate-fade-in" data-testid="wizard-step-basic">
        {/* Version badge */}
        <div className={`flex items-center gap-3 p-3 rounded-lg border-2 ${isPro ? "border-[#FF7300] bg-[#FF7300]/5" : "border-[#09090B] bg-[#F9FAFB]"}`}>
          <div className={`w-8 h-8 rounded-md flex items-center justify-center ${isPro ? "bg-[#FF7300]" : "bg-[#09090B]"}`}>
            {isPro ? <Crown size={16} className="text-white" /> : <Zap size={16} className="text-white" />}
          </div>
          <div>
            <div className="text-sm font-bold text-[#09090B]">{isPro ? "PRO Edition" : "Free Edition"}</div>
            <div className="text-xs text-[#A1A1AA]">{isPro ? "v2.0.0 — 28 files" : "v1.0.0 — 19 files"}</div>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="affiliate-tag" className="text-sm font-semibold text-[#09090B] flex items-center gap-2">
            <Tag size={14} className="text-[#FF7300]" /> Amazon Affiliate Tag
          </Label>
          <Input id="affiliate-tag" data-testid="input-affiliate-tag" placeholder="e.g. yourtag-20"
            value={config.affiliate_tag} onChange={(e) => updateConfig("affiliate_tag", e.target.value)} className="h-10" />
          <p className="text-xs text-[#A1A1AA]">Your Amazon Associates tracking ID.</p>
        </div>

        <div className="space-y-3">
          <Label className="text-sm font-semibold text-[#09090B] flex items-center gap-2">
            <Globe size={14} className="text-[#FF7300]" /> Enabled Amazon Domains
          </Label>
          <div className="grid gap-2">
            {DOMAINS.map((d) => (
              <label key={d.id} data-testid={`domain-${d.id}`}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                  config.enabled_domains.includes(d.id) ? "border-[#FF7300] bg-[#FF7300]/5" : "border-[#E4E4E7] hover:border-[#A1A1AA]"
                }`}>
                <Checkbox checked={config.enabled_domains.includes(d.id)} onCheckedChange={() => toggleDomain(d.id)} />
                <div className="flex-1">
                  <div className="text-sm font-medium text-[#09090B]">{d.label}</div>
                  <div className="text-xs text-[#A1A1AA]">{d.flag} &middot; {d.currency}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
      </div>
    );

    // Step 1: Sync & Features (both versions)
    if (step === 1) return (
      <div className="space-y-6 animate-fade-in" data-testid="wizard-step-advanced">
        <div className="space-y-2">
          <Label className="text-sm font-semibold text-[#09090B] flex items-center gap-2">
            <RefreshCw size={14} className="text-[#FF7300]" /> Sync Frequency
          </Label>
          <Select value={String(config.sync_interval_hours)} onValueChange={(val) => updateConfig("sync_interval_hours", parseInt(val))}>
            <SelectTrigger className="h-10" data-testid="select-sync-frequency"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SYNC_OPTIONS.map((opt) => (<SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="batch-size" className="text-sm font-semibold text-[#09090B]">Sync Batch Size</Label>
          <Input id="batch-size" data-testid="input-batch-size" type="number" min={1} max={50}
            value={config.sync_batch_size} onChange={(e) => updateConfig("sync_batch_size", parseInt(e.target.value) || 10)} className="h-10 w-24" />
        </div>
        <div className="flex items-center justify-between p-3 rounded-lg border border-[#E4E4E7]">
          <div><div className="text-sm font-semibold text-[#09090B]">Price History Tracking</div>
            <div className="text-xs text-[#A1A1AA]">Show sparkline charts on product cards</div></div>
          <Switch data-testid="switch-price-history" checked={config.price_history} onCheckedChange={(val) => updateConfig("price_history", val)} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="disclaimer" className="text-sm font-semibold text-[#09090B] flex items-center gap-2">
            <FileText size={14} className="text-[#FF7300]" /> Disclaimer Text
          </Label>
          <textarea id="disclaimer" data-testid="input-disclaimer" rows={2} value={config.disclaimer_text}
            onChange={(e) => updateConfig("disclaimer_text", e.target.value)}
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none" />
        </div>
      </div>
    );

    // Step 2 (PRO only): PRO Settings
    if (isPro && step === 2) return (
      <div className="space-y-4 animate-fade-in" data-testid="wizard-step-pro">
        <div className="flex items-center gap-2 mb-2">
          <Crown size={16} className="text-[#FF7300]" />
          <span className="text-sm font-bold text-[#FF7300]">PRO-Exclusive Settings</span>
        </div>
        {[
          { key: "smart_sync", icon: BarChart3, label: "Smart Sync", desc: "High-click products sync more frequently", val: config.smart_sync },
          { key: "link_cloaking", icon: Link2, label: "Link Cloaking", desc: "Clean /go/product-name URLs with click tracking", val: config.link_cloaking },
          { key: "sticky_cta", icon: Shield, label: "Sticky Buy CTA", desc: "Fixed bottom bar on product pages", val: config.sticky_cta },
          { key: "schema_enabled", icon: FileText, label: "SEO Schema", desc: "JSON-LD Product schema markup", val: config.schema_enabled },
          { key: "exit_intent", icon: Zap, label: "Exit Intent Popup", desc: "Show deal popup when user leaves", val: config.exit_intent },
          { key: "countdown_enabled", icon: RefreshCw, label: "Countdown Timer", desc: "Urgency timer on product cards", val: config.countdown_enabled },
          { key: "dark_mode", icon: Moon, label: "Dark Mode", desc: "Dark theme for frontend output", val: config.dark_mode },
        ].map((item) => {
          const Icon = item.icon;
          return (
            <div key={item.key} className="flex items-center justify-between p-3 rounded-lg border border-[#E4E4E7] hover:border-[#FF7300]/30 transition-colors">
              <div className="flex items-center gap-3">
                <Icon size={16} className="text-[#52525B]" />
                <div>
                  <div className="text-sm font-semibold text-[#09090B]">{item.label}</div>
                  <div className="text-xs text-[#A1A1AA]">{item.desc}</div>
                </div>
              </div>
              <Switch checked={item.val} onCheckedChange={(val) => updateConfig(item.key, val)} data-testid={`switch-${item.key}`} />
            </div>
          );
        })}
        {config.link_cloaking && (
          <div className="space-y-2 pl-1">
            <Label className="text-xs font-semibold text-[#52525B]">Link Prefix</Label>
            <Input value={config.link_prefix} onChange={(e) => updateConfig("link_prefix", e.target.value)}
              className="h-8 w-32 text-xs" placeholder="go" data-testid="input-link-prefix" />
            <p className="text-[10px] text-[#A1A1AA]">URLs: yoursite.com/{config.link_prefix}/product-name</p>
          </div>
        )}
      </div>
    );

    // Last step: Review
    return (
      <div className="space-y-5 animate-fade-in" data-testid="wizard-step-review">
        <div className="text-center mb-2">
          <div className={`w-14 h-14 rounded-full ${isPro ? "bg-[#FF7300]/10" : "bg-[#09090B]/10"} flex items-center justify-center mx-auto mb-3`}>
            {isPro ? <Crown size={28} className="text-[#FF7300]" /> : <Check size={28} className="text-[#09090B]" />}
          </div>
          <h3 className="font-display font-bold text-lg text-[#09090B]">Ready to Download</h3>
          <p className="text-sm text-[#52525B]">{isPro ? "Amazon Affiliate PRO" : "Amazon Affiliate Plugin"} — review your config.</p>
        </div>
        <div className="bg-[#F9FAFB] rounded-lg p-4 space-y-2.5 text-sm">
          <Row label="Edition" value={<span className={`font-bold ${isPro ? "text-[#FF7300]" : "text-[#09090B]"}`}>{isPro ? "PRO v2.0" : "Free v1.0"}</span>} />
          <Separator />
          <Row label="Affiliate Tag" value={config.affiliate_tag || <span className="text-[#A1A1AA] italic">Not set</span>} />
          <Separator />
          <Row label="Domains" value={config.enabled_domains.join(", ")} />
          <Separator />
          <Row label="Sync" value={`Every ${config.sync_interval_hours}h / ${config.sync_batch_size} batch`} />
          <Separator />
          <Row label="Price History" value={config.price_history ? "Enabled" : "Disabled"} green={config.price_history} />
          {isPro && (
            <>
              <Separator />
              <Row label="Smart Sync" value={config.smart_sync ? "ON" : "OFF"} green={config.smart_sync} />
              <Separator />
              <Row label="Link Cloaking" value={config.link_cloaking ? `/${config.link_prefix}/...` : "OFF"} green={config.link_cloaking} />
              <Separator />
              <Row label="Sticky CTA" value={config.sticky_cta ? "ON" : "OFF"} green={config.sticky_cta} />
              <Separator />
              <Row label="SEO Schema" value={config.schema_enabled ? "ON" : "OFF"} green={config.schema_enabled} />
              <Separator />
              <Row label="Dark Mode" value={config.dark_mode ? "ON" : "OFF"} green={config.dark_mode} />
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[560px] p-0 gap-0 overflow-hidden max-h-[90vh]" data-testid="config-wizard-dialog">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle className="font-display font-bold text-xl text-[#09090B] flex items-center gap-2">
            {isPro ? <Crown size={20} className="text-[#FF7300]" /> : <Sparkles size={20} className="text-[#FF7300]" />}
            Configure {isPro ? "PRO" : "Your"} Plugin
          </DialogTitle>
          <DialogDescription className="text-sm text-[#52525B]">
            Customize settings. Your ZIP will come pre-configured.
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 pb-4">
          <div className="flex items-center gap-1">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const isActive = i === step;
              const isDone = i < step;
              return (
                <div key={i} className="flex items-center flex-1">
                  <div className="flex items-center gap-2 flex-1">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                      isActive ? "bg-[#FF7300] text-white" : isDone ? "bg-[#09090B] text-white" : "bg-[#E4E4E7] text-[#A1A1AA]"
                    }`}>{isDone ? <Check size={13} /> : <Icon size={13} />}</div>
                    <span className={`text-xs font-medium hidden sm:inline ${isActive ? "text-[#09090B]" : "text-[#A1A1AA]"}`}>{s.label}</span>
                  </div>
                  {i < STEPS.length - 1 && <div className={`h-[2px] flex-1 mx-2 rounded ${i < step ? "bg-[#09090B]" : "bg-[#E4E4E7]"}`} />}
                </div>
              );
            })}
          </div>
        </div>

        <Separator />

        <div className="px-6 py-6 min-h-[300px] max-h-[50vh] overflow-y-auto">
          {renderStepContent()}
        </div>

        <Separator />

        <div className="px-6 py-4 flex items-center justify-between">
          <button data-testid="wizard-back-btn" onClick={() => step > 0 ? setStep(step - 1) : handleClose()}
            className="flex items-center gap-2 text-sm font-medium text-[#52525B] hover:text-[#09090B] transition-colors border-0 bg-transparent cursor-pointer px-3 py-2">
            <ArrowLeft size={14} />{step === 0 ? "Cancel" : "Back"}
          </button>
          {downloadError && (
            <div className="text-xs text-red-600 font-medium mr-auto" data-testid="download-error">{downloadError}</div>
          )}
          {step < lastStep ? (
            <button data-testid="wizard-next-btn" onClick={() => setStep(step + 1)}
              className="flex items-center gap-2 bg-[#09090B] text-white hover:bg-[#FF7300] transition-all rounded-md px-5 py-2.5 text-sm font-semibold border-0 cursor-pointer">
              Next <ArrowRight size={14} />
            </button>
          ) : (
            <button data-testid="wizard-download-btn" onClick={handleDownload} disabled={downloading}
              className={`flex items-center gap-2 ${isPro ? "bg-[#FF7300] hover:bg-[#E66800]" : "bg-[#09090B] hover:bg-[#27272A]"} text-white transition-all rounded-md px-6 py-2.5 text-sm font-bold border-0 cursor-pointer disabled:opacity-60`}>
              <Download size={14} />{downloading ? "Generating..." : `Download ${isPro ? "PRO" : "Plugin"} ZIP`}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, green }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-[#52525B]">{label}</span>
      <span className={`font-medium ${green === true ? "text-[#067d06]" : green === false ? "text-[#A1A1AA]" : "text-[#09090B]"}`}>
        {typeof value === "string" ? value : value}
      </span>
    </div>
  );
}
